import { useHandleResponse } from '@/utils/useHandleResponse';
import {
  ClearOutlined,
  DeleteFilled,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  LinkOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Card, Col, DatePicker, Form, Input, Modal, Row, Select, Spin, Table, Tag, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthProvider';
import { setContract } from '../../../../contextRedux/Contract/ContractModel';
import {
  setSelectedRecords,
  updateField,
  updatePropsAll,
} from '../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { setSelectedMentenancePlannings } from '../../../../contextRedux/MentenancePlanning/MentenancePlanningStore';
import CompanyService from '../../../../services/company/CompanyService';
import ContractService from '../../../../services/contract/ContractService';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import { RootState } from '../../../../store';
import { usePermission } from '../../../../utils/usePermission';
import MaintenanceModal from '../MentenancePlanningModal/MentenancePlanningModal';
import MentenancePlanningHistoryModal from './MentenancePlanningHistoryModal/MentenancePlanningHistoryModal';

const STORAGE_KEY = 'mentenance_planning_selections';
const EXPIRATION_TIME = 3 * 60 * 1000;

const MentanancePlanningAllTab: React.FC<any> = ({ isEditable }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [editForm] = Form.useForm();
  const reduxMentenancePlanning = useSelector((state: RootState) => state?.models?.mentenancePlanning);
  const mentenancePlannings = useSelector((state: RootState) => state?.store?.mentenancePlanning?.selectionData ?? []);

  const searchTerm = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsAll?.searchTerm);
  const skip = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsAll?.skip);
  const take = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsAll?.take);
  const refresh = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsAll?.refresh);

  const tehnicians = useSelector((state: RootState) => state?.store.user.data).filter((user: any) =>
    user?.userPermission?.rols?.some((role: string) =>
      ['tehnician', 'senior_tehnician', 'external_tehnician'].includes(role),
    )
  );

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const currentPage = Math.floor(reduxMentenancePlanning?.propsAll?.skip / reduxMentenancePlanning?.propsAll?.take) + 1;

  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  // const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false); // Track loading state for the button

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isLastMentenanceModalVisible, setLastMentenanceIsModalVisible] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);

  const [selectedMaintenances, setSelectedMaintenances] = useState<any>([]);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [selectedRecordEdit, setSelectedRecordEdit] = useState<any>(null);

  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<any>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<any>(null);
  const [workPlaces, setWorkPlaces] = useState<any[]>([]);
  const [systemTypes, setSystemTypes] = useState<any[]>([]);

  const { profile } = useAuth();

  // Add this state for contract search
  const [searchTermContract, setSearchTermContract] = useState<string>('');
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);

  // Add new state for period filter
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);

  // Add periods constant
  const PERIODS = ['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere'];
  const pageSizes = [10, 20, 30, 50];
  const currentDate = moment().format('YYYY-MM-DD');

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<any>({});
  const hasSelectedWorkPlace = selectedRecordEdit
    ? !!formValues?.workPlace?.name
    : Array.isArray(formValues?.workPlaceIds) && formValues.workPlaceIds.length > 0;
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();

  // Move usePermission calls to top level to ensure consistent hook order
  const hasAdminPermission = usePermission(['admin']);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updatePropsAll({ field: 'searchTerm', value: termInUrl }));
    dispatch(updatePropsAll({ field: 'skip', value: calculatedSkip }));
    dispatch(updatePropsAll({ field: 'take', value: pageSizeInUrl }));

    // Maintain selected records when filters change
    const currentSelectedRecords = mentenancePlannings.filter((record) =>
      reduxMentenancePlanning.selectedRecords.some((selected) => selected.number === record.number)
    );
    dispatch(setSelectedRecords(currentSelectedRecords));

    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take, mentenancePlannings]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    isFetching: isMentenancePlanningLoading,
    refetch: refetch,
  } = useQuery({
    queryKey: [
      'mentenancePlannings',
      searchTerm,
      skip,
      take,
      refresh,
      selectedMonth,
      dateRange,
      sortOrder,
      selectedPeriod,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
    ],
    queryFn: () =>
      MentenancePlanningService.searchAll({
        statusIds: [1],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
        month: selectedMonth ? selectedMonth.month() + 1 : undefined,
        year: selectedMonth ? selectedMonth.year() : undefined,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        sortDirection: sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined,
        periodicity: selectedPeriod || undefined,
      }).then((data) => {
        dispatch(updatePropsAll({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setSelectedMentenancePlannings(data?.response));
        dispatch(updatePropsAll({ field: 'hasMore', value: data?.response.length === take }));
        dispatch(updatePropsAll({ field: 'refresh', value: false }));
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
  });

  const { data: contracts, isFetching: isContractsLoading } = useQuery({
    queryKey: ['contracts'],
    queryFn: () =>
      ContractService.search({
        isActive: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }).then((data) => {
        dispatch(setContract(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: systemTypess, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const workPlacess = useMemo(() => {
    if (!selectedCompany) return [];
    const company = companies?.find((c: any) => c.name === selectedCompany);
    return company?.workPlaces || [];
  }, [companies, selectedCompany]);

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const { mutate: getMentenancePlanningByWorkPlace, isPending: isGetingMentenancePlanningByWorkPlace } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.searchMentenancePlanningByWorkPlace(formData),
    onSuccess: (data: any) => {
      if (!data) {
        console.error('Received data is not in JSON format or JSON parsing failed.');
        return;
      }
      return data?.response;
    },
    onError: (error: any) => {
      message.error(`Error processing your request: ${error.message}`);
    },
  });

  const { mutate: saveMentenancePlanning, isPending: isSavingMentenancePlanning } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, `Mentenanta ${data?.response?.number} a fost planificata cu succes`);
        // Refetch with current filters and pagination
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const { mutate: saveMentenancePlanningMultiple, isPending: isSavingMultipleMentenancePlanning } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.upsertMultiple(formData),
    onSuccess: (data: any) => {
      if (data) {
        const createdCount = data?.response?.length ?? 0;
        handleResponse(data, `${createdCount} mentenante`);
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });
  useEffect(() => {
    const savedData = loadFromStorage();
    if (savedData && savedData.length > 0 && savedData.length !== reduxMentenancePlanning.selectedRecords.length) {
      dispatch(updatePropsAll({ field: 'selectedRecords', value: savedData }));
      dispatch(setSelectedRecords(savedData));
    }
  }, [dispatch, reduxMentenancePlanning.selectedRecords.length]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updatePropsAll({ field: 'searchTerm', value }));
      dispatch(updatePropsAll({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch]
  );

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value);
    },
    [dispatch]
  );

  const handleViewLastMentenance = (record: any) => {
    setSelectedRecord(record); // Store the selected record
    getMentenancePlanningByWorkPlace(
      {
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        isInterventionFileIncluded: true,
        workPlaceId: record.workPlace.id,
        statusIds: [4],
      }, // Pass the workplace ID
      {
        onSuccess: (data: any) => {
          const records = data?.response || [];

          // Find max number
          const maxNumber = Math.max(...records.map((x: any) => x.interventionFile.number));

          // Find record with max number
          const selected = records.find((x: any) => x.interventionFile.number === maxNumber.toString());

          setSelectedRecord(selected); // Set the most recent maintenance
          setLastMentenanceIsModalVisible(true); // Show the modal
        },
        onError: (error: any) => {
          message.error(`Failed to fetch maintenances: ${error.message}`);
        },
      }
    );
  };

  const handleMonthChange = useCallback(
    (date: Dayjs | null) => {
      setSelectedMonth(date);
      dispatch(updatePropsAll({ field: 'skip', value: 0 }));
      // Maintain selections when filter changes
      const currentSelectedRecords = mentenancePlannings.filter((record) =>
        reduxMentenancePlanning.selectedRecords.some((selected) => selected.number === record.number)
      );
      dispatch(setSelectedRecords(currentSelectedRecords));
    },
    [dispatch, mentenancePlannings, reduxMentenancePlanning.selectedRecords]
  );

  const handleViewHistory = (record: any) => {
    getMentenancePlanningByWorkPlace(
      { workPlaceId: record.workPlace.id, statusIds: [4], isInterventionFileIncluded: true },
      {
        onSuccess: (data: any) => {
          setSelectedMaintenances(
            data?.response?.slice().sort((a: any, b: any) => b.interventionFile.number - a.interventionFile.number)
          );
          setIsModalVisible(true); // Show the modal
        },
        onError: (error: any) => {
          message.error(`Failed to fetch maintenances: ${error.message}`);
        },
      }
    );
  };

  const handleEdit = (record?: any) => {
    setSelectedRecordEdit(record || null);
    setIsEditModalVisible(true);

    if (record) {
      const relatedContract = contracts?.find((c: any) => c.id === record.contractId);
      if (relatedContract) {
        setSelectedContract(relatedContract);

        // Set workPlaces and systemTypes as before
        const uniqueWorkPlaces = Array.from(
          new Map(
            relatedContract.contractWorkPlaceItems?.map((item: any) => [item.workPlace.id, item.workPlace])
          ).values()
        );
        setWorkPlaces(uniqueWorkPlaces);

        const uniqueSystemTypes = Array.from(
          new Map(
            relatedContract.contractWorkPlaceItems?.map((item: any) => [item.systemType.id, item.systemType])
          ).values()
        );
        setSystemTypes(uniqueSystemTypes);

        // Set available periods
        const hasLaCerere = relatedContract.contractWorkPlaceItems?.some((item: any) => item.period === 'la cerere');

        if (hasLaCerere) {
          setAvailablePeriods(['la cerere']);
        } else {
          setAvailablePeriods(['lunar', 'trimestrial', 'semestrial', 'anual']);
        }
      }

      const formData = {
        contract: relatedContract,
        company: record?.company || undefined,
        workPlace: record?.workPlace || undefined,
        workPlaceIds: record?.workPlace?.name ? [record.workPlace.name] : [],
        mentenancePlanningSystemTypes:
          record.mentenancePlanningSystemTypes?.map((type: any) => type?.systemType?.name) || [],
        periodicity: record?.periodicity || undefined,
        dateEnd: record.dateEnd ? new Date(record.dateEnd).toISOString().split('T')[0] : undefined,
        assignedTechnician: record?.assignedTechnician || undefined,
        appointment: record?.appointment ? new Date(record.appointment).toISOString().split('T')[0] : undefined,
        intervalTimetable: record?.intervalTimetable || undefined,
        finalDate: record?.finalDate ? new Date(record.finalDate).toISOString().split('T')[0] : undefined,
        interventionFileId: record?.interventionFileId,
        lastMentenance: record?.lastMentenance,
        history: record?.history,
      };

      editForm.setFieldsValue(formData);
      setFormValues(formData);
    } else {
      // Reset everything for new maintenance
      setSelectedContract(null);
      setWorkPlaces([]);
      setSystemTypes([]);
      setAvailablePeriods([]);
      editForm.resetFields();
      setFormValues({});
    }
  };

  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      const statusId =
        tehnicians.find((tehnician: any) => tehnician?.name == values.assignedTechnician?.name) != undefined ? 2 : 1;
      const selectedWorkPlaceNames = selectedRecordEdit
        ? [values.workPlace?.name].filter(Boolean)
        : (values.workPlaceIds ?? []);
      const selectedWorkPlaceIds = workPlaces
        .filter((workPlace: any) => selectedWorkPlaceNames.includes(workPlace?.name))
        .map((workPlace: any) => workPlace.id);
      const workPlaceId = selectedWorkPlaceIds[0];
      const assignedTechnicianId = tehnicians.find(
        (tehnician: any) => tehnician?.name == values.assignedTechnician?.name
      )?.id;

      if (!workPlaceId) {
        message.error('Selectati cel putin un punct de lucru.');
        return;
      }

      const mappedSystemTypes = values.mentenancePlanningSystemTypes.map((selectedTypeName: string) => {
        const systemType = systemTypes.find((type: any) => type.name === selectedTypeName);

        const existingEntry = selectedRecordEdit?.mentenancePlanningSystemTypes?.find(
          (existing: any) => existing.systemType.name === selectedTypeName
        );

        return {
          id: existingEntry?.id || '',
          mentenancePlanningId: existingEntry?.mentenancePlanningId || selectedRecordEdit?.id || '',
          systemType: systemType,
          systemTypeId: systemType?.id,
        };
      });

      const formData = {
        id: selectedRecordEdit?.id || '',
        contractId: selectedRecordEdit ? selectedRecordEdit.contractId : selectedContract.id,
        statusId: statusId,
        companyId: selectedContract?.company?.id,
        workPlaceId: workPlaceId,
        workPlaceIds: selectedWorkPlaceIds,
        assignedTechnicianId: assignedTechnicianId,
        mentenancePlanningSystemTypes: mappedSystemTypes,
        interventionFileId: values?.interventionFileId,
        lastMentenance: values?.lastMentenance,
        intervalTimetable: values?.intervalTimetable,
        registeredByUserId: profile?.id,
        history: values?.history ?? [`[Inregistrata] - ${dayjs().format('DD-MM-YYYY HH:mm')}]`],
        appointment: values?.appointment,
        dateEnd: values?.dateEnd,
        periodicity: values?.periodicity,
        finalDate: values?.finalDate,
      };

      if (!selectedRecordEdit && selectedWorkPlaceIds.length > 1) {
        saveMentenancePlanningMultiple(formData);
      } else {
        saveMentenancePlanning(formData);
      }
      setIsEditModalVisible(false);
      dispatch(updatePropsAll({ field: 'refresh', value: true }));
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleSelectContract = (contractId: string) => {
    const selectedContract = contracts.find((c: any) => c.id === contractId);

    if (selectedContract) {
      setSelectedContract(selectedContract);

      // Extract unique workPlaces and systemTypes as before
      const uniqueWorkPlaces = Array.from(
        new Map(
          selectedContract.contractWorkPlaceItems?.map((item: any) => [item.workPlace.id, item.workPlace])
        ).values()
      );
      setWorkPlaces(uniqueWorkPlaces);

      const uniqueSystemTypes = Array.from(
        new Map(
          selectedContract.contractWorkPlaceItems?.map((item: any) => [item.systemType.id, item.systemType])
        ).values()
      );
      setSystemTypes(uniqueSystemTypes);

      const uniquePeriods: any = Array.from(
        new Set(selectedContract.contractWorkPlaceItems?.map((item: any) => item.period))
      );
      // Determine available periods based on contract items
      const hasLaCerere = selectedContract.contractWorkPlaceItems?.some((item: any) => item.period === 'la cerere');

      if (hasLaCerere) {
        setAvailablePeriods(['la cerere']);
      } else {
        setAvailablePeriods(uniquePeriods);
      }

      // Update form with contract info and reset periodicity
      const formData = {
        contract: selectedContract,
        company: selectedContract.company,
        finalDate: selectedContract.dateEnd
          ? new Date(selectedContract.dateEnd).toISOString().split('T')[0]
          : undefined,
        workPlace: undefined,
        workPlaceIds: [],
        mentenancePlanningSystemTypes: [],
        periodicity: undefined, // Reset periodicity when changing contract
      };
      editForm.setFieldsValue(formData);
      setFormValues(formData);
    }
  };

  const updateAvailablePeriodsForWorkPlaces = (workPlaceNames: string[]) => {
    if (!selectedContract || !Array.isArray(workPlaceNames) || workPlaceNames.length === 0) {
      setAvailablePeriods([]);
      return;
    }

    const selectedWorkPlaceIds = workPlaces
      .filter((workPlace: any) => workPlaceNames.includes(workPlace.name))
      .map((workPlace: any) => workPlace.id);

    const uniquePeriods: any = Array.from(
      new Set(
        selectedContract.contractWorkPlaceItems
          ?.filter((cwpi: any) => selectedWorkPlaceIds.includes(cwpi.workPlace?.id))
          ?.map((item: any) => item.period)
      )
    );

    const hasLaCerere = uniquePeriods.includes('la cerere');
    if (hasLaCerere) {
      setAvailablePeriods(['la cerere']);
    } else {
      setAvailablePeriods(uniquePeriods);
    }
  };

  const handleSelectWorkPlace = (workPlaceName: any) => {
    updateAvailablePeriodsForWorkPlaces([workPlaceName]);
  };

  const handleSelectWorkPlaces = (workPlaceNames: string[]) => {
    updateAvailablePeriodsForWorkPlaces(workPlaceNames ?? []);
  };

  const handleSelectAllWorkPlaces = () => {
    const allWorkPlaceNames = workPlaces.map((workPlace: any) => workPlace.name);
    editForm.setFieldsValue({ workPlaceIds: allWorkPlaceNames });
    setFormValues((prev: any) => ({ ...prev, workPlaceIds: allWorkPlaceNames }));
    handleSelectWorkPlaces(allWorkPlaceNames);
  };

  const handleClearAllWorkPlaces = () => {
    editForm.setFieldsValue({ workPlaceIds: [] });
    setFormValues((prev: any) => ({ ...prev, workPlaceIds: [] }));
    handleSelectWorkPlaces([]);
  };

  const handleSelectAllSystemTypes = () => {
    const allSystemTypeNames = systemTypes.map((systemType: any) => systemType.name);
    editForm.setFieldsValue({ mentenancePlanningSystemTypes: allSystemTypeNames });
    setFormValues((prev: any) => ({ ...prev, mentenancePlanningSystemTypes: allSystemTypeNames }));
  };

  const handleClearAllSystemTypes = () => {
    editForm.setFieldsValue({ mentenancePlanningSystemTypes: [] });
    setFormValues((prev: any) => ({ ...prev, mentenancePlanningSystemTypes: [] }));
  };

  // Update the handler for date range changes
  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const newDateRange: [Dayjs | null, Dayjs | null] = dates || [null, null];
    setDateRange(newDateRange);
    dispatch(updatePropsAll({ field: 'skip', value: 0 }));
    // Maintain selections when filter changes
    const currentSelectedRecords = mentenancePlannings.filter((record) =>
      reduxMentenancePlanning.selectedRecords.some((selected) => selected.number === record.number)
    );
    dispatch(setSelectedRecords(currentSelectedRecords));
  };

  // Add handler for period changes
  const handlePeriodChange = (value: string | null) => {
    setSelectedPeriod(value);
    dispatch(updatePropsAll({ field: 'skip', value: 0 }));
    // Maintain selections when filter changes
    const currentSelectedRecords = mentenancePlannings.filter((record) =>
      reduxMentenancePlanning.selectedRecords.some((selected) => selected.number === record.number)
    );
    dispatch(setSelectedRecords(currentSelectedRecords));
  };

  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const sanitizeData = (data: any[]) => {
    return data.map((item) => ({
      id: item.id,
      number: item.number,
      company: item.company,
      companyId: item.company?.id,
      workPlace: item.workPlace,
      workPlaceId: item.workPlace?.id,
      mentenancePlanningSystemTypes: item.mentenancePlanningSystemTypes,
      appointment: item.appointment,
      intervalTimetable: item.intervalTimetable,
      assignedTechnician: item.assignedTechnician,
      assignedTechnicianId: item.assignedTechnician?.id,
      periodicity: item.periodicity,
      dateEnd: item.dateEnd,
      finalDate: item.finalDate,
      lastMentenance: item.lastMentenance,
    }));
  };

  const saveToStorage = (data: any[]) => {
    const storageData = {
      data: sanitizeData(data),
      timestamp: Date.now(),
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
    } catch (error) {
      console.error('Error saving to storage:', error);
    }
  };

  const loadFromStorage = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const { data, timestamp } = JSON.parse(stored);

      // Check if data is expired
      if (Date.now() - timestamp > EXPIRATION_TIME) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error loading from storage:', error);
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  const handleDeleteRecord = useCallback(
    async (record: any) => {
      try {
        const response = await MentenancePlanningService.delete(record.id);
        if (response?.errors?.length) {
          // Handle errors if any
          console.error('Failed to delete record:', response.errors);
        } else {
          // Refresh the data after deletion
          message.success(`Mentenanta ${record.number} a fost stearsa`);
          dispatch(updatePropsAll({ field: 'refresh', value: true }));
        }
      } catch (error) {
        console.error('Error deleting record:', error);
      }
    },
    [reduxMentenancePlanning]
  );

  const handleBulkDelete = async () => {
    try {
      const selectedRecords = reduxMentenancePlanning.selectedRecords;
      const deletePromises = selectedRecords.map((record: any) => MentenancePlanningService.delete(record.id));

      await Promise.all(deletePromises);

      message.success(`${selectedRecords.length} mentenante au fost sterse cu succes`);
      setIsDeleteModalVisible(false);
      handleClearSelection();
      dispatch(updatePropsAll({ field: 'refresh', value: true }));
    } catch (error) {
      console.error('Error deleting records:', error);
      message.error('A apărut o eroare la ștergerea mentenanțelor');
    }
  };

  const baseColumns = [
    { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color={'blue'}>{number}</Tag> },
    {
      title: 'Beneficiar',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    {
      title: 'Punct',
      render: (record: any) => {
        return <>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</>;
      },
    },
    {
      title: 'Sistem',
      dataIndex: 'mentenancePlanningSystemTypes',
      key: 'mentenancePlanningSystemTypes',
      render: (mentenancePlanningSystemTypes: any[]) => (
        <>
          {mentenancePlanningSystemTypes && mentenancePlanningSystemTypes.length > 0 && (
            <span>
              {mentenancePlanningSystemTypes.length > 0
                ? mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')
                : ''}
            </span>
          )}
        </>
      ),
    },
    {
      title: 'Numar Contract',
      dataIndex: 'contract',
      render: (contract: any) => {
        return (
          <>
            <Button
              onClick={() => window.open(`/service-maintenance/contract/edit?id=${contract?.id}`, '_blank')}
              disabled={!usePermission(['admin', 'manager', 'dispatcher', 'sales', 'senior_sales'])}
              icon={<LinkOutlined />}
            >
              {contract?.number}
            </Button>
          </>
        );
      },
    },
    { title: 'Periodicitate', dataIndex: 'periodicity' },

    {
      title: 'Termen limita',
      dataIndex: 'dateEnd',
      sorter: true, // Enable sorting
      sortOrder: sortOrder,
      render: (_value: any, record: any) => (
        <span>{record.dateEnd ? dayjs(record.dateEnd).format('DD-MM-YYYY') : '-'}</span>
      ),
    },
    {
      title: 'Data Expirare Contract',
      dataIndex: 'finalDate',
      render: (_value: any, record: any) => (
        <span>{record.finalDate ? dayjs(record.finalDate).format('DD-MM-YYYY') : '-'}</span>
      ),
    },
    {
      title: 'Ultima Mentenanta',
      dataIndex: 'lastMentenance',
      render: (_value: any, record: any) => (
        <>
          {!!record.lastMentenance ? (
            <Button onClick={() => handleViewLastMentenance(record)}>
              <span>{dayjs(record.lastMentenance).format('DD-MM-YYYY')}</span>
            </Button>
          ) : (
            <span>-</span>
          )}
        </>
      ),
    },
    {
      title: 'Programare',
      dataIndex: '',
      render: (_value: any, record: any) => (
        <span>{record.appointment ? dayjs(record.appointment).format('DD-MM-YYYY') : '-'}</span>
      ),
    },
    {
      title: 'Istoric',
      dataIndex: '',
      render: (record: any) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button icon={<HistoryOutlined />} onClick={() => handleViewHistory(record)}></Button>
          </div>
        );
      },
    },
    {
      title: 'Actiuni',
      dataIndex: '',
      render: (record: any) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Tooltip title='Editati mentenanta'>
              <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} disabled={!isEditable} />
            </Tooltip>

            <Tooltip title='Ștergeți mentenanta'>
              <Button
                disabled={!hasAdminPermission}
                onClick={() => handleDeleteRecord(record)}
                icon={<DeleteOutlined />}
              ></Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: reduxMentenancePlanning.selectedRecords.map((record: any) => record.number),
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      // Previously selected records from storage (with full data)
      const storedSelections = loadFromStorage() ?? [];

      // Map existing selected rows by number
      const selectionsMap = new Map<string, any>();
      storedSelections.forEach((record: any) => {
        selectionsMap.set(record.number, record);
      });

      // Update selections with rows that are now selected on the current page
      selectedRows.forEach((row: any) => {
        selectionsMap.set(row.number, row);
      });

      // Remove rows that are visible but now deselected
      mentenancePlannings.forEach((row: any) => {
        if (!selectedRowKeys.includes(row.number)) {
          selectionsMap.delete(row.number);
        }
      });

      const updatedSelection = Array.from(selectionsMap.values());

      dispatch(updatePropsAll({ field: 'selectedRecords', value: updatedSelection }));
      dispatch(setSelectedRecords(updatedSelection));
      saveToStorage(updatedSelection);
    },
    getCheckboxProps: () => ({
      disabled: !isEditable,
    }),
  };

  // Add handler for table change (sorting)
  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    if (sorter.field === 'dateEnd') {
      setSortOrder(sorter.order);
    } else {
      setSortOrder(null);
    }
  };

  const handleClearSelection = () => {
    dispatch(updatePropsAll({ field: 'selectedRecords', value: [] }));
    dispatch(setSelectedRecords([]));
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const onDateChange = (dateField: string, dateValue: string) => {
    editForm.setFieldsValue({ [dateField]: dateValue });
  };

  const handleFormValuesChange = (_changedValues: any, allValues: any) => {
    setFormValues(allValues);
  };

  // Add pagination configuration
  const pagination = {
    current: currentPage,
    pageSize: take,
    total: reduxMentenancePlanning?.propsAll.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updatePropsAll({ field: 'skip', value: skipValue }));
      dispatch(updatePropsAll({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updatePropsAll({ field: 'skip', value: 0 }));
      dispatch(updatePropsAll({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  if (
    isSavingMentenancePlanning ||
    isSavingMultipleMentenancePlanning ||
    isMentenancePlanningLoading ||
    isGetingMentenancePlanningByWorkPlace ||
    isContractsLoading ||
    isCompaniesLoading ||
    isSystemTypesLoading
  ) {
    return <Spin className='spinner' size='large'></Spin>;
  }

  return (
    <div className='table-page-no-h-scroll'>
      <Card style={{ padding: '20px', marginBottom: '20px', boxShadow: '0px 4px 10px rgba(0,0,0,0.1)' }}>
        {profile?.userPermission?.rols?.includes('dispatcher') && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px' }}>
            <Tooltip title='Ștergeți toate selecțiile'>
              <Button
                type='default'
                onClick={handleClearSelection}
                disabled={reduxMentenancePlanning.selectedRecords.length === 0 || !isEditable}
                icon={<ClearOutlined />}
                shape='circle'
              />
            </Tooltip>
            {hasAdminPermission && (
              <Tooltip title={`Ștergeți mentenantele selectate (${reduxMentenancePlanning.selectedRecords.length})`}>
                <Button
                  type='primary'
                  danger
                  onClick={() => setIsDeleteModalVisible(true)}
                  disabled={reduxMentenancePlanning.selectedRecords.length === 0 || !hasAdminPermission}
                  icon={<DeleteFilled />}
                  shape='circle'
                />
              </Tooltip>
            )}
            <Tooltip title='Adauga mentenanta'>
              <Button
                disabled={!isEditable}
                type='primary'
                onClick={() => handleEdit(undefined)}
                icon={<PlusOutlined />}
                shape='circle'
              />
            </Tooltip>
          </div>
        )}
        <Row gutter={[16, 16]}>
          {/* Search input */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input.Search
              placeholder='Search...'
              defaultValue={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>

          {/* Month Picker */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <DatePicker
              value={selectedMonth}
              picker='month'
              placeholder='Filtreaza dupa luna'
              onChange={handleMonthChange}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>

          {/* Period Filter */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder='Filtreaza dupa periodicitate'
              value={selectedPeriod}
              onChange={handlePeriodChange}
              allowClear
              style={{ width: '100%' }}
            >
              {PERIODS.map((period) => (
                <Select.Option key={period} value={period}>
                  {period}
                </Select.Option>
              ))}
            </Select>
          </Col>

          {/* Date Range Picker */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <DatePicker.RangePicker
              value={[dateRange[0], dateRange[1]]}
              onChange={handleDateRangeChange}
              placeholder={['Data inceput', 'Data sfarsit']}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder='Filtreaza dupa beneficiar'
              value={selectedCompany}
              onChange={handleCompanyChange}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp='children'
            >
              {companies?.map((company: any) => (
                <Select.Option key={company?.id} value={company?.name}>
                  {company?.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder='Filtreaza dupa punct de lucru'
              value={selectedWorkPlace}
              onChange={handleWorkPlaceChange}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp='children'
            >
              {workPlacess.map((workPlace: any) => (
                <Select.Option key={workPlace?.id} value={workPlace?.name}>
                  {workPlace?.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder='Filtreaza dupa sistem'
              value={selectedSystemType}
              onChange={handleSystemTypeChange}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp='children'
            >
              {systemTypess?.map((systemType: any) => (
                <Select.Option key={systemType?.id} value={systemType?.name}>
                  {systemType?.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>
      <div className='table-scroll-wrapper'>
        <Table
          rowSelection={rowSelection}
          columns={baseColumns}
          dataSource={mentenancePlannings.map((item) => ({ ...item, key: item.number }))}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          loading={isMentenancePlanningLoading}
        />
      </div>

      <MentenancePlanningHistoryModal
        visible={isModalVisible}
        maintenances={selectedMaintenances} // All maintenances for the workplace
        onClose={() => setIsModalVisible(false)}
      />

      <MaintenanceModal
        isEditable={!isEditable}
        isModalVisible={isLastMentenanceModalVisible}
        handleModalClose={() => setLastMentenanceIsModalVisible(false)}
        selectedRecord={selectedRecord} // The most recent maintenance for the workplace
      />

      <Modal
        title={`${selectedRecordEdit ? 'Editare' : 'Creeaza'} Mentenanta`}
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={handleSave}
        okText='Salvează'
        cancelText='Anulează'
      >
        <Form form={editForm} layout='vertical' onValuesChange={handleFormValuesChange}>
          <Form.Item
            name={['contract', 'number']}
            label='Contract'
            rules={[{ required: true, message: 'Selectati un contract' }]}
          >
            <Select
              placeholder='Selectati contractul'
              onSelect={handleSelectContract}
              disabled={!!selectedRecordEdit || !isEditable} // Disable when editing
              showSearch
              filterOption={false}
              onSearch={(value) => setSearchTermContract(value)}
              value={editForm.getFieldValue('contract')?.id}
            >
              {contracts
                ?.filter(
                  (contract: any) =>
                    contract?.number?.toLowerCase().includes(searchTermContract.toLowerCase()) ||
                    contract?.company?.name?.toLowerCase().includes(searchTermContract.toLowerCase())
                )
                .map((contract: any) => {
                  const contractText = `${contract.number} - ${contract.company?.name}`;
                  return (
                    <Select.Option key={contract.id} value={contract.id}>
                      <Tooltip title={contractText}>
                        <span>{contractText}</span>
                      </Tooltip>
                    </Select.Option>
                  );
                })}
            </Select>
          </Form.Item>

          <Form.Item
            name={['company', 'name']}
            label='Beneficiar'
            rules={[{ required: true, message: 'Selectati un beneficiar' }]}
          >
            <Select disabled placeholder='Selectati beneficiarul'></Select>
          </Form.Item>

          {selectedRecordEdit ? (
            <Form.Item
              name={['workPlace', 'name']}
              label='Punct de lucru'
              rules={[{ required: true, message: 'Selectati un punct de lucru' }]}
            >
              <Select
                placeholder='Selectati punctul de lucru'
                onSelect={handleSelectWorkPlace}
                disabled={!selectedContract || !isEditable}
              >
                {workPlaces.map((workPlace: any) => (
                  <Select.Option key={workPlace.id} value={workPlace.name}>
                    {workPlace.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.Item
              name='workPlaceIds'
              label='Puncte de lucru'
              rules={[{ required: true, message: 'Selectati cel putin un punct de lucru' }]}
            >
              <Select
                mode='multiple'
                placeholder='Selectati punctele de lucru'
                onChange={handleSelectWorkPlaces}
                disabled={!selectedContract || !isEditable}
                allowClear
                onClear={handleClearAllWorkPlaces}
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      <Button type='link' size='small' onClick={handleSelectAllWorkPlaces} disabled={!workPlaces.length}>
                        Selecteaza toate
                      </Button>
                    </div>
                    {menu}
                  </>
                )}
              >
                {workPlaces.map((workPlace: any) => (
                  <Select.Option key={workPlace.id} value={workPlace.name}>
                    {workPlace.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name='mentenancePlanningSystemTypes'
            label='Sistem'
            rules={[{ required: true, message: 'Selectati sistemele' }]}
          >
            <Select
              mode='multiple'
              placeholder='Select sistemul'
              disabled={!selectedContract || !hasSelectedWorkPlace || !isEditable}
              allowClear
              onClear={handleClearAllSystemTypes}
              dropdownRender={(menu) => (
                <>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                    <Button
                      type='link'
                      size='small'
                      onClick={handleSelectAllSystemTypes}
                      disabled={!systemTypes.length || !hasSelectedWorkPlace}
                    >
                      Selecteaza toate
                    </Button>
                  </div>
                  {menu}
                </>
              )}
            >
              {systemTypes.map((systemType: any) => (
                <Select.Option key={systemType.id} value={systemType.name}>
                  {systemType.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name='periodicity'
            label='Periodicitate'
            rules={[{ required: true, message: 'Selectati un periodicitatea' }]}
          >
            <Select
              placeholder='Selectati periodicitatea'
              disabled={
                !selectedContract ||
                !hasSelectedWorkPlace ||
                !formValues?.mentenancePlanningSystemTypes ||
                availablePeriods.length === 0 ||
                !isEditable
              }
            >
              {availablePeriods.map((period: string) => (
                <Select.Option key={period} value={period}>
                  {period}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name='dateEnd'
            label='Termen limita'
            rules={[{ required: true, message: 'Selectati un termenul limita' }]}
          >
            <Input
              disabled={
                !selectedContract ||
                !hasSelectedWorkPlace ||
                !formValues?.mentenancePlanningSystemTypes ||
                !formValues?.periodicity ||
                !isEditable
              }
              type='date'
              min={currentDate}
              onChange={(e) => onDateChange('dateEnd', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name={['assignedTechnician', 'name']}
            label='Tehnician'
            dependencies={['appointment']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value && getFieldValue('appointment')) {
                    return Promise.reject(
                      new Error('Dacă selectați o zi de programare, trebuie să selectați și un tehnician.')
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Select
              disabled={
                !selectedContract ||
                !hasSelectedWorkPlace ||
                !formValues?.mentenancePlanningSystemTypes ||
                !formValues?.periodicity ||
                !formValues?.dateEnd ||
                !isEditable
              }
              placeholder='Selectati tehnicianul'
              allowClear
            >
              {tehnicians?.map((tehnician: any, index: any) => (
                <Select.Option key={index} value={tehnician?.name}>
                  {tehnician?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='appointment'
                label='Ziua programarii'
                dependencies={['assignedTechnician']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value && getFieldValue(['assignedTechnician', 'name'])) {
                        return Promise.reject(
                          new Error('Dacă selectați un tehnician, trebuie să setați și o zi de programare.')
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  disabled={
                    !selectedContract ||
                    !hasSelectedWorkPlace ||
                    !formValues?.mentenancePlanningSystemTypes ||
                    !formValues?.periodicity ||
                    !formValues?.dateEnd ||
                    !formValues?.assignedTechnician?.name ||
                    !isEditable
                  }
                  type='date'
                  min={currentDate}
                  onChange={(e) => onDateChange('appointment', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='intervalTimetable' label='Interval orar'>
                <Input
                  disabled={
                    !selectedContract ||
                    !hasSelectedWorkPlace ||
                    !formValues?.mentenancePlanningSystemTypes ||
                    !formValues?.periodicity ||
                    !formValues?.dateEnd ||
                    !formValues?.assignedTechnician?.name ||
                    !formValues?.appointment ||
                    !isEditable
                  }
                  placeholder='Ex: 09:00-12:00'
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name='finalDate' label='Data expirare contract'>
            <Input type='date' disabled min={currentDate} onChange={(e) => onDateChange('finalDate', e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='Confirmare ștergere'
        open={isDeleteModalVisible}
        onOk={handleBulkDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText='Da, șterge'
        cancelText='Anulează'
        okButtonProps={{ danger: true }}
      >
        <p>
          Sunteți sigur că doriți să ștergeți {reduxMentenancePlanning.selectedRecords.length} mentenanțe selectate?
        </p>
        <p>Această acțiune nu poate fi anulată.</p>
      </Modal>
    </div>
  );
};

export default MentanancePlanningAllTab;
